import CardRow from '../../ui/CardRow';
import Badge from '../../ui/Badge';
import Drawer from '../../ui/Drawer';
import { formatDateTime } from '../../utils/formatDateTime';
import { useI18n } from '../../i18n/context';
import { useState } from 'react';

const safeT = (t, key, fallback = '') => {
  const v = t(key);
  return v === key ? (fallback || key) : v;
};

const formatAccess = (org, t) => {
  const eff = org?.effective_access || {};
  if (!eff.effective_plan) return '—';
  const until = eff.effective_until
    ? ` · ${safeT(t, 'org.accessUntil', 'acceso hasta {date}').replace('{date}', formatDateTime(eff.effective_until))}`
    : '';
  const seatLabel = safeT(t, 'org.usersLabel', 'usuarios');
  const seatLimit = org.seat_limit ? ` · ${org.seat_limit} ${seatLabel}` : '';
  const planLabel = safeT(t, `org.planLabelMap.${eff.effective_plan}`, eff.effective_plan || '');
  return `${planLabel.toUpperCase()}${until}${seatLimit}`;
};

const formatSummary = (org, t) => {
  const trialDate = org.trial_end_at || org.trial_ends_at;
  const trial = trialDate ? `${safeT(t, 'orgCards.trialLabel')}: ${formatDateTime(trialDate)}` : null;
  const grace = org.grace_ends_at ? `${safeT(t, 'orgCards.graceLabel')}: ${formatDateTime(org.grace_ends_at)}` : null;
  const override = org.override_plan
    ? `${safeT(t, 'orgCards.overrideLabel')}: ${safeT(t, `org.planLabelMap.${org.override_plan}`, org.override_plan || '').toUpperCase()} · ${formatDateTime(org.override_ends_at)}`
    : null;
  const billing = org?.billing?.status
    ? `${safeT(t, 'billingOverride.label', 'Billing')}: ${safeT(t, `billingOverride.status.${org.billing.status}`, org.billing.status).toUpperCase()} · ${safeT(t, `billingOverride.source.${org.billing.source}`, org.billing.source)}${org.billing.override_active && org.billing.override_until ? ` · ${safeT(t, 'billingOverride.until', 'hasta {date}').replace('{date}', formatDateTime(org.billing.override_until))}` : ''}`
    : null;
  return [trial, grace, override, billing].filter(Boolean).join(' · ') || '—';
};

const planBadge = (org, t) => {
  const eff = org?.effective_access || {};
  const plan = safeT(t, `org.planLabelMap.${eff.effective_plan || org.plan || 'starter'}`, eff.effective_plan || org.plan || 'starter').toUpperCase();
  if (eff.effective_status === 'trial') return <Badge variant="warning">{safeT(t, 'org.badgeTrial', 'TRIAL')}</Badge>;
  if (['business', 'enterprise'].includes((eff.effective_plan || org.plan || '').toLowerCase())) {
    return <Badge variant="primary">{plan}</Badge>;
  }
  return <Badge variant="neutral">{plan}</Badge>;
};

export default function OrganizationRowCard({ org, onAction, showActionsInline }) {
  const { t } = useI18n();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const planLabel = (plan) => safeT(t, `org.planLabelMap.${plan}`, plan || '—');

  const stripeStatus = (org?.stripe_subscription_status || '').toLowerCase();
  const stripeTrialBlocked = !org?.stripe_subscription_id || ['canceled', 'incomplete_expired', 'unpaid'].includes(stripeStatus);
  const stripeTrialTitle = stripeTrialBlocked ? safeT(t, 'orgActions.stripeTrialBlocked', 'Stripe no permite modificar el trial en este estado') : '';

  const effAccess = org?.effective_access || {};
  const isTrialEffective =
    effAccess.effective_status === 'trial' || (org?.billing?.status || '').toLowerCase() === 'trial';
  const showQuickEndTrial = isTrialEffective && !stripeTrialBlocked;

  const detailsLabel = t('orgActions.viewDetails');
  const openStripeLabel = t('orgActions.openStripe');

  return (
    <div style={{ width: '100%' }}>
      <CardRow
        columns={[
          <div>
            <div className="ui-headline">
              <span>{org.name || '—'}</span>
              {org.deleted_at && <Badge variant="danger">ELIMINADA</Badge>}
              {planBadge(org, t)}
              {showQuickEndTrial && (
                <button
                  type="button"
                  className="ui-button secondary org-quick-end-trial"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction('endStripeTrial');
                  }}
                  disabled={stripeTrialBlocked}
                  title={safeT(t, 'orgActions.quickEndTrialTitle', safeT(t, 'orgActions.endStripeTrial'))}
                >
                  {safeT(t, 'orgActions.quickEndTrial', 'Fin trial')}
                </button>
              )}
            </div>
            <div className="ui-subtext">{org.id}</div>
          </div>,
          <div>
            <div className="ui-subtext">{t('orgCards.planTitle')}</div>
            <div>{planLabel(org.plan || '—').toUpperCase()} • {org.plan_source || '—'}</div>
          </div>,
          <div>
            <div className="ui-subtext">{t('orgCards.accessTitle')}</div>
            <div>{formatAccess(org, t)}</div>
          </div>,
          <div>
            <div className="ui-subtext">{t('orgCards.historyTitle')}</div>
            <div>{formatSummary(org, t)}</div>
          </div>,
          <div>
            <div className="ui-subtext">{safeT(t, 'users.adminEmail', safeT(t, 'org.adminEmail', 'Admin email'))}</div>
            <div>{org.admin_email || '—'}</div>
          </div>
        ]}
        actions={
          showActionsInline ? null : (
            <button className="ui-button ghost" onClick={() => onAction('showActionsInline')}>{t('actions.actions')}</button>
          )
        }
      />
      {showActionsInline && (
        <div className="org-actions-panel">
          <div className="org-actions-section">
            <div className="org-actions-title">{t('orgActions.sectionPrimary') || 'Acciones principales'}</div>
            <div className="actions-group">
              <button className="ui-button primary org-action-button" onClick={() => onAction('viewDetails')}>
                <span className="org-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M10 2a8 8 0 1 0 .001 16.001A8 8 0 0 0 10 2zm0 3.2a1.1 1.1 0 1 1 0 2.2 1.1 1.1 0 0 1 0-2.2zm1.4 9H8.6v-1.4h1V9.2H8.4V7.8h3V14.2z" />
                  </svg>
                </span>
                {detailsLabel}
              </button>
              <button className="ui-button primary org-action-button" onClick={() => onAction('openStripe')}>
                <span className="org-action-icon" aria-hidden="true">
                  <svg viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M3 5.5h14a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1zm0 2.4v5.6h14V7.9H3zm2.2 3.2h4.6v1.2H5.2v-1.2z" />
                  </svg>
                </span>
                {openStripeLabel}
              </button>
              <button className="ui-button primary org-action-button" onClick={() => onAction('editStripe')}>
                {t('orgActions.configureStripe')}
              </button>
              <button className="ui-button secondary org-action-button" onClick={() => onAction('forceSync')}>
                {t('orgActions.forceSync')}
              </button>
            </div>
          </div>

          <div className="org-actions-section">
            <div className="org-actions-title">{t('orgActions.sectionSubscriptionMgmt') || 'Gestión de suscripción'}</div>
            <div className="actions-group">
              <button className="ui-button secondary org-action-button" onClick={() => onAction('cancelSub')}>
                {t('orgActions.cancelSubscription')}
              </button>
              <button className="ui-button secondary org-action-button" onClick={() => onAction('pauseSub')}>
                {t('orgActions.pauseSubscription')}
              </button>
              <button className="ui-button secondary org-action-button" onClick={() => onAction('resumeSub')}>
                {t('orgActions.resumeSubscription')}
              </button>
              <button className="ui-button secondary org-action-button" onClick={() => onAction('setPlan')}>
                {t('org.planChangeTitle')}
              </button>
            </div>
          </div>

          <div className="org-actions-section">
            <div className="org-actions-title">{t('orgActions.sectionTrialGrace') || 'Trial / gracia'}</div>
            <div className="actions-group">
              <button className="ui-button secondary org-action-button" onClick={() => onAction('setTrial')} disabled={stripeTrialBlocked} title={stripeTrialTitle}>
                {t('orgActions.setTrial')}
              </button>
              <button className="ui-button secondary org-action-button" onClick={() => onAction('grace7')} disabled={stripeTrialBlocked} title={stripeTrialTitle}>
                {t('orgActions.grant7')}
              </button>
              <button className="ui-button secondary org-action-button" onClick={() => onAction('grace14')} disabled={stripeTrialBlocked} title={stripeTrialTitle}>
                {t('orgActions.grant14')}
              </button>
              <button className="ui-button secondary org-action-button" onClick={() => onAction('grace30')} disabled={stripeTrialBlocked} title={stripeTrialTitle}>
                {t('orgActions.grant30')}
              </button>
              <button className="ui-button secondary org-action-button" onClick={() => onAction('endStripeTrial')} disabled={stripeTrialBlocked} title={stripeTrialTitle}>
                {t('orgActions.endStripeTrial')}
              </button>
              <button className="ui-button secondary org-action-button" onClick={() => onAction('clearBillingOverride')}>
                {t('billingOverride.clear')}
              </button>
            </div>
          </div>

          <div className="org-actions-section">
            <div className="org-actions-title">{t('orgActions.sectionDangerZone') || 'Zona de riesgo'}</div>
            <div className="actions-group">
              <button className="ui-button danger org-action-button" onClick={() => onAction('delete')}>
                {t('org.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
